import React, { useCallback, useMemo, useState } from "react";
import { Card, Table, Title } from "@yoast/ui-library";
import * as styles from "./TwoYearOffer.module.scss";

import yoastIcon from "../../icons/yoast-icon.svg";
import useRequest from "../../reactHooks/useRequest";
import { getUserId } from "shared-frontend/functions/auth";
import groupBy from "lodash/groupBy";
import { SubscriptionRow } from "./SubscriptionRow";
import classnames from "classnames";


// the jsdoc plugin can't handle ts imports

/**
 * A component that displays a 2-year renewal offer for eligible subscriptions.
 * @returns {Element} The TwoYearOffer component.
 */
const TwoYearOffer = () => {
	const userId = getUserId();
	const request = useRequest( `Customers/${ userId }/subscriptions/biennial-eligible` );
	/** @type {import("../../../../nestjs-server/src/models/Subscription").Subscription[]} */
	const eligibleSubscriptionsResponse = request.data;

	const eligibleSubscriptions = useMemo(
		() => Object.values( groupBy( eligibleSubscriptionsResponse, "subscriptionNumber" ) ),
		[ eligibleSubscriptionsResponse ],
	);

	const [ hiddenSubscriptions, setHiddenSubscriptions ] = useState( [] );

	const hideSubscription = useCallback( ( subscriptionNumber ) => {
		setHiddenSubscriptions( hiddenSubscriptions.concat( subscriptionNumber ) );
	}, [ hiddenSubscriptions ] );

	const unhideSubscription = useCallback( ( subscriptionNumber ) => {
		setHiddenSubscriptions( hiddenSubscriptions.filter( ( number ) => number !== subscriptionNumber ) );
	}, [ hiddenSubscriptions ] );

	const allIsHidden = useMemo( () => {
		if ( eligibleSubscriptions.length === 0 ) {
			return true;
		}
		return eligibleSubscriptions.every( ( subscriptionsWithSameNumber ) =>
			hiddenSubscriptions.includes( subscriptionsWithSameNumber[ 0 ].subscriptionNumber ),
		);
	}, [ eligibleSubscriptions, hiddenSubscriptions ] );


	if ( eligibleSubscriptions.length === 0 ) {
		// No eligible subscriptions, nothing to display
		return null;
	}

	return (
		<Card className={ classnames( styles.twoYearOfferCard, { [ styles.hidden ]: allIsHidden } ) }>
			<div className={ styles.container }>
				<div>
					<img src={ yoastIcon } alt="" />
				</div>
				<div className={ styles.tableContainer }>
					<Title className={ styles.title }>
						15% off for our most loyal customers
					</Title>
					<p>
						One or more of your subscriptions is about to expire! As a thank-you for being a loyal customer,
						enjoy a one-time offer: <strong>save 15%</strong> when you renew for two years.
					</p>
					<Table className={ styles.table }>
						<Table.Head>
							<Table.Row>
								<Table.Header>Subscription number</Table.Header>
								<Table.Header>Product(s)</Table.Header>
								<Table.Header>Expires on</Table.Header>
								<Table.Header>Current renewal</Table.Header>
								<Table.Header>2-year renewal</Table.Header>
							</Table.Row>
						</Table.Head>
						<Table.Body>
							{ eligibleSubscriptions.map( ( subscriptionsWithSameNumber, index ) =>
								<SubscriptionRow
									onHide={ hideSubscription }
									onUnhide={ unhideSubscription }
									isHidden={ hiddenSubscriptions.includes( subscriptionsWithSameNumber[ 0 ].subscriptionNumber ) }
									key={ index }
									subscriptions={ subscriptionsWithSameNumber }
								/>,
							) }
						</Table.Body>
					</Table>
				</div>
			</div>
		</Card>
	);
};

export default TwoYearOffer;
